/**
 * CtaBand — Single source of truth for repeated CTA patterns
 * Mobile-safe, responsive, no overflow
 */

interface CtaBandProps {
  heading: string;
  subheading?: string;
  primaryCta: {
    label: string;
    href: string;
    icon?: string;
  };
  secondaryCta?: {
    label: string;
    href: string;
    icon?: string;
  };
  variant?: 'dark' | 'light' | 'amber';
  icon?: string;
  badge?: string;
  steps?: Array<{ n: string; label: string }>;
}

export default function CtaBand({
  heading,
  subheading,
  primaryCta,
  secondaryCta,
  variant = 'dark',
  icon,
  badge,
  steps,
}: CtaBandProps) {
  const variantStyles = {
    dark: {
      container: 'bg-[#1A2540]',
      accent: 'bg-[#F5C842]',
      accentText: 'text-[#1A2540]',
      heading: 'text-[#FDF6E3]',
      subheading: 'text-[rgba(253,246,227,0.65)]',
      badge: 'text-[#F5C842]',
      primaryBtn: 'bg-[#F5C842] text-[#1A2540] hover:bg-[#e0b430]',
      secondaryBtn: 'bg-[rgba(245,200,66,0.10)] border border-[rgba(245,200,66,0.25)] text-[#FDF6E3] hover:bg-[rgba(245,200,66,0.18)]',
      stepBg: 'bg-[rgba(245,200,66,0.08)] border-[rgba(245,200,66,0.18)]',
      stepText: 'text-[#FDF6E3]',
      stepNumber: 'bg-[#F5C842] text-[#1A2540]',
    },
    light: {
      container: 'bg-[#FDFAF4]',
      accent: 'bg-[#F5C842]',
      accentText: 'text-[#1E1A14]',
      heading: 'text-[#1A2332]',
      subheading: 'text-[#4A5568]',
      badge: 'text-[#D97706]',
      primaryBtn: 'bg-[#D97706] text-white hover:bg-[#B45309]',
      secondaryBtn: 'bg-white text-[#1A2332] hover:bg-[#F7F5F0] border border-[#E8E2D6]',
      stepBg: 'bg-[#F7F5F0] border-[#E8E2D6]',
      stepText: 'text-[#1A2332]',
      stepNumber: 'bg-[#D97706] text-white',
    },
    amber: {
      container: 'bg-[#F5C842]',
      accent: 'bg-[#1E1A14]',
      accentText: 'text-[#FDF6E3]',
      heading: 'text-[#1E1A14]',
      subheading: 'text-[#1E1A14]/75',
      badge: 'text-[#1E1A14]',
      primaryBtn: 'bg-[#1E1A14] text-[#FDF6E3] hover:bg-[#2C2518]',
      secondaryBtn: 'bg-white text-[#1E1A14] hover:bg-[#FDFAF4]',
      stepBg: 'bg-[#1E1A14]/10 border-[#1E1A14]/20',
      stepText: 'text-[#1E1A14]',
      stepNumber: 'bg-[#1E1A14] text-[#FDF6E3]',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`${styles.container} rounded-2xl overflow-hidden shadow-lg`}>
      <div className="flex flex-col md:flex-row items-center gap-0">
        {/* Left accent strip with icon */}
        {icon && (
          <div className={`hidden md:flex flex-col items-center justify-center ${styles.accent} px-6 py-8 self-stretch gap-2 flex-shrink-0`}>
            <i className={`${icon} ${styles.accentText} text-4xl`} aria-hidden="true"></i>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 px-6 py-6 md:px-8">
          {badge && (
            <p className={`${styles.badge} text-xs font-bold uppercase tracking-widest mb-1`}>
              {badge}
            </p>
          )}
          <h3
            className={`${styles.heading} font-black text-xl md:text-2xl leading-snug mb-2`}
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            {heading}
          </h3>
          {subheading && (
            <p className={`${styles.subheading} text-sm leading-relaxed max-w-lg`}>
              {subheading}
            </p>
          )}

          {/* Optional steps */}
          {steps && steps.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {steps.map((s) => (
                <div
                  key={s.n}
                  className={`flex items-center gap-2 ${styles.stepBg} border rounded-full px-3 py-1.5`}
                >
                  <span
                    className={`w-4 h-4 flex items-center justify-center ${styles.stepNumber} rounded-full text-[10px] font-black flex-shrink-0`}
                  >
                    {s.n}
                  </span>
                  <span className={`${styles.stepText} text-xs font-medium whitespace-nowrap`}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col items-center gap-2 px-6 pb-6 md:py-6 md:pr-8 flex-shrink-0">
          <a
            href={primaryCta.href}
            className={`inline-flex items-center gap-2 ${styles.primaryBtn} px-7 py-3 rounded-full font-black text-sm transition-all shadow-lg whitespace-nowrap cursor-pointer`}
          >
            {primaryCta.icon && <i className={primaryCta.icon} aria-hidden="true"></i>}
            {primaryCta.label}
          </a>
          {secondaryCta && (
            <a
              href={secondaryCta.href}
              className={`inline-flex items-center gap-2 ${styles.secondaryBtn} px-7 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap cursor-pointer`}
            >
              {secondaryCta.icon && <i className={secondaryCta.icon} aria-hidden="true"></i>}
              {secondaryCta.label}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}